import classNames from "classnames";
import uniqueId from "lodash/uniqueId";
import { Typography } from "PFComponents/typography";
import { EventKey } from "PFTypes/event_key";
import { ComponentType, KeyboardEvent, PropsWithChildren, ReactNode, useCallback } from "react";

import { Animate, Flex, Stack } from "../containers";
import css from "./accordion.module.scss";
import { IconAccordionState, IconSet } from "./parts/icon_accordion_state";
import { useAccordionState } from "./use_accordion_state";

export type AccordionState = "open" | "closed";
type HeaderVariant = "bodyBold" | "h4" | "h5";

export type AccordionStateComponentProps = { isOpen: boolean; iconSet?: IconSet };

export type AccordionProps = {
  header?: ReactNode;
  subtitle?: ReactNode;
  defaultState?: AccordionState;
  state?: AccordionState;
  onStateChange?: (state: AccordionState) => void;
  headerVariant?: HeaderVariant;
  headerFullWidth?: boolean;
  iconSet?: IconSet;
  actions?: React.ReactNode;
  animated?: boolean;
  trigger?: boolean;
  keepMounted?: boolean;
  classes?: { root?: string; header?: string; content?: string };
  qaId?: string;
  StateComponent?: ComponentType<AccordionStateComponentProps>;
};

export const Accordion = ({
  header,
  subtitle,
  defaultState = "open",
  state,
  onStateChange,
  children,
  headerVariant = "h4",
  headerFullWidth,
  iconSet = "caret",
  actions,
  animated = true,
  keepMounted,
  classes,
  qaId,
  StateComponent = IconAccordionState
}: PropsWithChildren<AccordionProps>) => {
  const contentId = uniqueId("accordion-content-");
  const headerId = uniqueId("accordion-header-");

  const { toggle, isOpen } = useAccordionState({
    onStateChange,
    defaultState,
    state
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== EventKey.Enter && event.key !== EventKey.Space) {
        return;
      }

      event.preventDefault();
      toggle();
    },
    [toggle]
  );

  const Wrapper = animated ? Animate : "div";

  return (
    <Wrapper className={classNames(css.root, classes?.root)}>
      {!!header && (
        <Flex alignItems="center" gap="spacingMd" className={classes?.header}>
          <div
            data-qa-id={qaId}
            className={classNames(css.headerTitle, { [css.headerFullWidth]: headerFullWidth })}
            id={headerId}
            role="button"
            aria-expanded={isOpen}
            aria-controls={contentId}
            onClick={toggle}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            <StateComponent isOpen={isOpen} iconSet={iconSet} />
            <Stack
              gap="spacingXs"
              className={classNames(css.headerWrapper, { [css.headerFullWidth]: headerFullWidth })}
            >
              <Typography variant={headerVariant}>{header}</Typography>
              {subtitle && (
                <Typography variant="bodyRegular" className={css.subtitle}>
                  {subtitle}
                </Typography>
              )}
            </Stack>
          </div>
          {actions && <div>{actions}</div>}
        </Flex>
      )}
      {(isOpen || keepMounted) && (
        <div
          role="region"
          id={contentId}
          aria-labelledby={headerId}
          inert={!isOpen}
          className={classNames(css.content, { [css.closed]: !isOpen })}
        >
          <div className={classNames(!!header && css.contentMargin, classes?.content)}>{children}</div>
        </div>
      )}
    </Wrapper>
  );
};
